import { observer } from 'mobx-react-lite'
import parse from 'html-react-parser'
import classNames from 'classnames'
import { useEffect, useState } from 'react'
import { Button, Icon, SpinnerLoader, Typography } from 'shared/ui'
import { useEnhanceRequest } from 'entities/AIAgent/actions/enhancePrompt/useEnhanceRequest'
import { textToHtml } from 'entities/Message/lib/textToHtml'
import { useEntitiesContext } from 'entities/EntitiesExport'
import styles from './styles.module.scss'

export type IEnhancedPromptModalProps = {
  prompt: string
  requestEnhance: (prompt: string, signal?: AbortSignal) => Promise<string>
  onEditPrompt?: () => void
  onAcceptPrompt: (prompt: string) => void
  onClose: () => void
  setError: (value: string) => void
}

export const EnhancedPromptModal = observer(
  ({
    prompt,
    onEditPrompt,
    requestEnhance,
    onClose,
    onAcceptPrompt,
    setError,
  }: IEnhancedPromptModalProps) => {
    const entities = useEntitiesContext()
    const { enhance, loading } = useEnhanceRequest({ requestEnhance, setError, prompt })
    const [improvedPrompt, setImprovedPrompt] = useState('')
    const onEnhance = async () => {
      const improved = await enhance()
      if (improved) setImprovedPrompt(improved)
    }

    useEffect(() => {
      onEnhance()
    }, [])

    const handleEditPrompt = () => {
      onEditPrompt?.()
      onClose()
    }
    const handleAcceptPrompt = () => {
      onAcceptPrompt(improvedPrompt)
      onClose()
    }

    const getPromptContent = (prompt: string, ariaLabel: string) => {
      return (
        <Typography ariaLabel={ariaLabel} className={styles.promptText} variant='body-rg-regular'>
          {parse(
            textToHtml(entities, {
              text: prompt,
              replaceNewRow: true,
              replaceActionChips: true,
            })
          )}
        </Typography>
      )
    }

    return (
      <>
        <div className={styles.content}>
          <div className={styles.section}>
            <div className={classNames(styles.sectionHeader, styles.sectionCurrentHeader)}>
              <Typography variant='heading-lg' ariaLabel='CurrentVersion'>
                Current version
              </Typography>
              <Button
                text='Edit'
                size='small'
                typeBtn='contained'
                contained='secondary'
                icon='edit'
                onClick={handleEditPrompt}
                disabled={loading}
              />
            </div>
            {getPromptContent(prompt, 'PreviousPromptContent')}
          </div>
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <Icon icon='stars' color='var(--purple-70)' fontSize={22} />
              <Typography
                variant='heading-lg'
                ariaLabel='NewVersionTitle'
                className={styles.headerText}
              >
                {loading ? 'Enhancing prompt...' : 'New version'}
              </Typography>
            </div>
            {loading ? (
              <div className={styles.loading}>
                <SpinnerLoader ariaLabel={'EnhancedPromptModal'} />
              </div>
            ) : (
              getPromptContent(improvedPrompt, 'EnhancedPromptContent')
            )}
          </div>
        </div>
        <div className={styles.actions}>
          <Button
            text='Try again'
            typeBtn='contained'
            contained='secondary'
            size='medium'
            icon='refresh'
            onClick={onEnhance}
            disabled={loading}
          />
          <Button
            text='I like it'
            typeBtn='contained'
            contained='primary'
            size='medium'
            icon='check1'
            onClick={handleAcceptPrompt}
            disabled={loading || !improvedPrompt}
          />
        </div>
      </>
    )
  }
)
